import React, {
  Fragment,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  createStyles,
  makeStyles,
  Theme,
  Typography
} from '@material-ui/core';
import {connect, useDispatch, useSelector} from 'react-redux';

// components, variable styles, constants
import NavRoutes from 'src/constants/route/NavRoutes';
import {APP, DEFAULT_GREY_COLOR, GET_8_PIXEL_BOX} from 'src/style/variable';
import BackNavigation from '../backNavigation/BackNavigation';

// styles
import './PaymentsDetailsStyles.scss';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import {fetchPatientsInvoiceLines} from '@shared/services/Payment.service';
import {PatientInvoiceLineMap} from '@shared/services/service-mappers/PaymentServiceMapper';
import HelperUtils from '@shared/utils/HelperUtils';
import {
  _guarantorLogin,
  _patientPaymentDetailsId,
  _setAppointmentPatientId,
  _setUpdateScheduledAppointment
} from '@shared/store/actions/PaymentActions';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import Loader from '@components/framework/loader/Loader';
import GridTable, {
  DEFAULT_GRID_TABLE__OPTIONS
} from '@components/system/GridTable/GridTable';
import {
  dateOfPaymentCell,
  headerRightAlign,
  paymentTableHeader,
  tableEvenRow,
  tableOddRow
} from 'src/modules/admin/modules/Invoices/TableStyles';
import VaccineCard from 'src/modules/appointment-schedular/components/VaccineCard/VaccineCard';
import {TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER} from '@shared/constants/Mapping';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {
  getAllAppointmentsInPortal,
  getAvailableVaccine
} from '@shared/services/AppointmentSchedulingService';
import {GetAppointmentsResponse} from '@shared/services/service-mappers/AppointmentSchedulingMapper';
import {getPatientTicketInPortal} from '@shared/services/WorkflowServices';
import {ITicketDetailsPortal} from '@shared/services/service-mappers/WorkflowMapper';
import LabelValue from 'src/modules/appointment-schedular/components/LabelValue/LabelValue';
import {appointmentsDateTimeFormat} from '@shared/utils/DateTimeUtils';
import {_setAvailableVaccinesList} from '@shared/store/actions/AppointmentSchedularAction';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ScheduleAppointmentChoiceDialog from 'src/modules/appointment-schedular/components/ScheduleAppointmentChoiceDialog/ScheduleAppointmentChoiceDialog';

const {innerWidth: width} = window;
const isDesktop = width >= 1024 ? true : false;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loginButton: {
      padding: `${GET_8_PIXEL_BOX(1)} 0`,
      fontFamily: APP.FONT_FAMILY.BOLD,
      height: '2.7rem'
    },
    card: {
      borderRadius: 0,
      color: theme.palette.primary.contrastText,
      boxShadow: 'none'
    },
    cardPadding: {
      '& .MuiCardContent-root': {
        padding: 0
      },
      padding: 0
    },
    paymentDateView: {
      flex: 1,
      flexDirection: 'column',
      display: 'flex'
    },
    description: {
      color: DEFAULT_GREY_COLOR,
      fontSize: '0.9rem'
    },
    lineHeight: {
      lineHeight: '1.3rem'
    }
  })
);

const AppointmentSection = ({
  scrollIntoView = false
}: {
  scrollIntoView?: boolean;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const {token} = useParams<{token: string}>();
  const history = useHistory();
  const patientDetails = useSelector(
    (state: any) => state.payments.patientPaymentDetailsId
  );

  const clinicDetails = useSelector(
    (state: any) => state.payments.clinicDetails
  );
  const locations = useSelector(
    (state: any) => state.payments.locations || []
  );
  const [showScheduleChoiceDialog, setShowScheduleChoiceDialog] =
    useState(false);

  const voiceAssistantShortCode = useMemo(() => {
    const patientClinicId = patientDetails?.clinic?.id;
    if (patientClinicId === clinicDetails?.clinic_details?.id) {
      const clinicShortCode = clinicDetails?.clinic_details?.short_code;
      if (clinicShortCode) {
        return clinicShortCode;
      }
    }

    if (patientClinicId && Array.isArray(locations) && locations.length > 0) {
      const matchedLocation = locations.find(
        (location: {id: number; short_code?: string}) =>
          location.id === patientClinicId
      );
      return matchedLocation?.short_code || '';
    }

    return '';
  }, [
    patientDetails?.clinic?.id,
    clinicDetails?.clinic_details?.id,
    clinicDetails?.clinic_details?.short_code,
    locations
  ]);

  useEffect(() => {
    if (wrapperRef.current && scrollIntoView) {
      wrapperRef.current.scrollIntoView();
    }
  }, [scrollIntoView]);
  const dispatch = useDispatch();

  const navigateToAppointmentScheduling = (): void => {
    dispatch(
      _setAppointmentPatientId({
        patientId: patientDetails.id,
        clinicId: patientDetails?.clinic?.id
      })
    );

    dispatch(
      _setUpdateScheduledAppointment({
        updateAppointment: false
      })
    );

    history.push(
      `/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.BASE}/${token}/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.SCHEDULE_APPOINTMENT}`,
      {
        fromPaymentPortal: true
      }
    );
  };

  const openScheduleChoiceDialog = (): void => {
    setShowScheduleChoiceDialog(true);
  };

  const navigateToVoiceAssistant = (): void => {
    if (!voiceAssistantShortCode) {
      return;
    }
    setShowScheduleChoiceDialog(false);
    const path = `/${NavRoutes.PUBLIC.VOICE_ASSISTANT}/${voiceAssistantShortCode}`;
    window.open(
      `${window.location.origin}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleSelectNormalScheduling = (): void => {
    setShowScheduleChoiceDialog(false);
    navigateToAppointmentScheduling();
  };

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [appointments, setAppointments] = useState({
    isLoading: false,
    list: [] as GetAppointmentsResponse[]
  });

  const init = async () => {
    if (!patientDetails.id || !patientDetails?.clinic?.id) return;
    try {
      setAppointments(p => ({...p, isLoading: true}));
      const res = await getAllAppointmentsInPortal({
        params: {
          patient_id: patientDetails.id,
          clinic_id: patientDetails?.clinic?.id
        }
      });
      setAppointments(p => ({...p, list: res.data.rows}));
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
      setAppointments(p => ({...p, list: []}));
    } finally {
      setAppointments(p => ({...p, isLoading: false}));
    }
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDetails.id, patientDetails?.clinic?.id]);

  useEffect(() => {
    const fetchAvailableVaccine = async () => {
      if (!patientDetails?.clinic?.id) return;
      try {
        const vaccinesResponse = await getAvailableVaccine({
          params: {clinic_id: patientDetails?.clinic?.id}
        });
        if (vaccinesResponse.data?.length) {
          dispatch(_setAvailableVaccinesList(vaccinesResponse.data));
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      }
    };
    fetchAvailableVaccine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDetails?.clinic?.id]);

  return (
    <div
      id="appointments"
      ref={wrapperRef}
      className="section-wrapper appointment-section">
      <div className="header-wrapper">
        <Typography className="patient-name input-header">
          {t('PAYMENTS_LOGIN.DASHBOARD.APPOINTMENTS_LABEL')}
        </Typography>
        {patientDetails.id && (
          <Button
            onClick={openScheduleChoiceDialog}
            variant="outlined"
            color="primary">
            {t('PAYMENTS_LOGIN.DASHBOARD.SCHEDULE_APPOINTMENT_BUTTON_LABEL')}
          </Button>
        )}
      </div>
      {appointments.isLoading && <Loader />}
      {!appointments.isLoading && appointments.list.length > 0 && (
        <p className="no-item-text">
          {t('PAYMENTS_LOGIN.DASHBOARD.EDIT_BUTTON_LABEL')}
        </p>
      )}
      {!appointments.isLoading && appointments.list.length > 0 && (
        <div className="vaccines-cards-wrapper">
          {appointments.list.map(appointment => {
            return (
              <VaccineCard
                key={appointment.id}
                appointmentObj={appointment}
                showPatientName={false}
                clinicZone={clinicDetails?.clinic_details?.zone}
                clinicId={patientDetails?.clinic?.id}
              />
            );
          })}
        </div>
      )}
      {!appointments.isLoading && appointments.list.length === 0 && (
        <p className="no-item-text">
          {t('PAYMENTS_LOGIN.DASHBOARD.NO_APPOINTMENTS_LABEL')}
        </p>
      )}
      <ScheduleAppointmentChoiceDialog
        open={showScheduleChoiceDialog}
        onClose={() => setShowScheduleChoiceDialog(false)}
        onSelectAssistant={navigateToVoiceAssistant}
        onSelectNormal={handleSelectNormalScheduling}
        voiceAssistantShortCode={voiceAssistantShortCode}
      />
    </div>
  );
};

const PriorAuthorizationSection = ({
  scrollIntoView = false
}: {
  scrollIntoView?: boolean;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (wrapperRef.current && scrollIntoView) {
      wrapperRef.current.scrollIntoView({behavior: 'smooth'});
    }
  });

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [activePriorAuthorizations, setActivePriorAuthorizations] = useState({
    isLoading: false,
    list: [] as ITicketDetailsPortal[]
  });

  const [pastPriorAuthorizations, setPastPriorAuthorizations] = useState({
    isLoading: false,
    list: [] as ITicketDetailsPortal[]
  });

  const patientDetails = useSelector(
    (state: any) => state.payments.patientPaymentDetailsId
  );

  const fetchActivePriorAuth = async () => {
    if (!patientDetails.id || !patientDetails?.clinic?.id) return;
    try {
      setActivePriorAuthorizations(p => ({...p, isLoading: true}));
      const res = await getPatientTicketInPortal({
        patientId: patientDetails.id,
        params: {
          module:
            TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[
              'PRIOR_AUTHORIZATION'
            ],
          clinic_id: patientDetails?.clinic?.id,
          active_prior_auth: true
        }
      });
      if (res.data.rows) {
        setActivePriorAuthorizations(p => ({...p, list: res.data.rows}));
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
      setActivePriorAuthorizations(p => ({...p, list: []}));
    } finally {
      setActivePriorAuthorizations(p => ({...p, isLoading: false}));
    }
  };

  const fetchPastPriorAuth = async () => {
    if (!patientDetails.id || !patientDetails?.clinic?.id) return;
    try {
      setPastPriorAuthorizations(p => ({...p, isLoading: true}));
      const res = await getPatientTicketInPortal({
        patientId: patientDetails.id,
        params: {
          module:
            TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[
              'PRIOR_AUTHORIZATION'
            ],
          clinic_id: patientDetails?.clinic?.id,
          active_prior_auth: false
        }
      });
      if (res.data.rows) {
        setPastPriorAuthorizations(p => ({...p, list: res.data.rows}));
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
      setPastPriorAuthorizations(p => ({...p, list: []}));
    } finally {
      setPastPriorAuthorizations(p => ({...p, isLoading: false}));
    }
  };

  const clinicDetails = useSelector(
    (state: any) => state.payments.clinicDetails
  );

  const renderPriorAuthCard = (priorAuth: ITicketDetailsPortal) => {
    return (
      <div className="prior-auth-card">
        <p className="prior-auth-card-title">{priorAuth.title}</p>
        <LabelValue
          label={'Appointment Date'}
          value={
            priorAuth.appointment_date
              ? appointmentsDateTimeFormat(
                  priorAuth.appointment_date,
                  clinicDetails?.clinic_details?.zone ?? 'America/Chicago'
                )
              : t('COMMON.NONE_VALUE')
          }
        />
        <LabelValue
          label={t('PAYMENTS_LOGIN.DASHBOARD.STATUS_LABEL')}
          value={priorAuth.category ?? priorAuth.status}
        />
      </div>
    );
  };

  useEffect(() => {
    fetchActivePriorAuth();
    fetchPastPriorAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDetails.id, patientDetails?.clinic?.id]);

  return (
    <div
      id="prior-authorizations"
      ref={wrapperRef}
      className="section-wrapper prior-authorization-section">
      <Typography className="patient-name input-header">
        {t('PAYMENTS_LOGIN.DASHBOARD.ACTIVE_PRIOR_AUTHORIZATION_LABEL')}
      </Typography>
      <div className="prior-auth-card-wrapper">
        {activePriorAuthorizations.isLoading && <Loader />}
        {!activePriorAuthorizations.isLoading &&
          activePriorAuthorizations.list.length > 0 &&
          activePriorAuthorizations.list.map((priorAuth, idx) => {
            return (
              <Fragment key={idx}>{renderPriorAuthCard(priorAuth)}</Fragment>
            );
          })}
        {!activePriorAuthorizations.isLoading &&
          activePriorAuthorizations.list.length === 0 && (
            <p className="no-item-text">
              {t(
                'PAYMENTS_LOGIN.DASHBOARD.NO_ACTIVE_PRIOR_AUTHORIZATION_LABEL'
              )}
            </p>
          )}
      </div>
      <div className="past-prior-section-wrapper">
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className="section-title-text">
              {t('PAYMENTS_LOGIN.DASHBOARD.PAST_PRIOR_AUTHORIZATION_LABEL')}{' '}
              {pastPriorAuthorizations.isLoading ? (
                <Loader />
              ) : (
                `(${pastPriorAuthorizations.list.length})`
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className="prior-auth-card-wrapper">
              {pastPriorAuthorizations.isLoading && <Loader />}
              {!pastPriorAuthorizations.isLoading &&
                pastPriorAuthorizations.list.length > 0 &&
                pastPriorAuthorizations.list.map((priorAuth, idx) => {
                  return (
                    <Fragment key={idx}>
                      {renderPriorAuthCard(priorAuth)}
                    </Fragment>
                  );
                })}
              {!pastPriorAuthorizations.isLoading &&
                pastPriorAuthorizations.list.length === 0 && (
                  <p className="no-item-text">
                    {t(
                      'PAYMENTS_LOGIN.DASHBOARD.NO_PAST_PRIOR_AUTHORIZATION_LABEL'
                    )}
                  </p>
                )}
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
};

export const AppointmentPaymentsDetails = (): JSX.Element => {
  const classes = useStyles();
  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const backNavigation = (): void => {
    dispatch(_patientPaymentDetailsId({}));
    if ('token' in params) {
      history.push(
        `/${NavRoutes.PUBLIC.PAYMENTS_DASHBOARD}/${params['token']}`
      );
    }
  };
  return (
    <div className="payments-details-wrapper">
      <Card
        className={['payments-details-content-wrap', classes.card].join(' ')}>
        <CardContent
          className={[classes.cardPadding, 'content'].join(' ')}
          style={{backgroundColor: '#fcfcfc'}}>
          <div className="payment-details-content-view">
            <Typography className="details-view" component={'div'}>
              <BackNavigation navigateTo={backNavigation} />
            </Typography>
            <div style={{paddingTop: '2rem'}}>
              <AppointmentSection />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const PriorAuthPaymentsDetails = (): JSX.Element => {
  const classes = useStyles();
  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const backNavigation = (): void => {
    dispatch(_patientPaymentDetailsId({}));
    if ('token' in params) {
      history.push(
        `/${NavRoutes.PUBLIC.PAYMENTS_DASHBOARD}/${params['token']}`
      );
    }
  };

  return (
    <div className="payments-details-wrapper">
      <Card
        className={['payments-details-content-wrap', classes.card].join(' ')}>
        <CardContent
          className={[classes.cardPadding, 'content'].join(' ')}
          style={{backgroundColor: '#fcfcfc'}}>
          <div className="payment-details-content-view">
            <Typography className="details-view" component={'div'}>
              <BackNavigation navigateTo={backNavigation} />
            </Typography>
            <div style={{paddingTop: '2rem'}}>
              <PriorAuthorizationSection />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const PaymentsDetails = (props: any): ReactElement => {
  const classes = useStyles();
  const history = useHistory();
  const params: any = useParams();
  const {t} = useTranslation();
  const {isLogin, defaultSectionId} = props;
  const balanceForwardData = useSelector(
    (state: any) => state.payments.balanceForwardData
  );
  let sectionId = defaultSectionId;
  const {search} = useLocation();
  const searchParams = new URLSearchParams(search ?? '');
  if (searchParams.get('sectionId')) {
    sectionId = searchParams.get('sectionId');
  }

  const patientDetails = useSelector(
    (state: any) => state.payments.patientPaymentDetailsId
  );
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [invoiceLines, setInvoiceLines] = React.useState<
    Array<PatientInvoiceLineMap>
  >([]);

  const backNavigation = (): void => {
    props._patientPaymentDetailsId({});
    if ('token' in params) {
      history.push(
        `/${NavRoutes.PUBLIC.PAYMENTS_DASHBOARD}/${params['token']}`
      );
    }
  };

  const clinicDetails = useSelector(
    (state: any) => state.payments.clinicDetails
  );

  // statements enable or not
  const statementsEnable: boolean = useSelector(
    (state: any) =>
      state.payments.clinicDetails?.clinic_group_details?.statements || false
  );
  React.useEffect(() => {
    if (!isLogin) {
      props._guarantorLogin(false);
      if ('token' in params) {
        history.push(
          `/${NavRoutes.PUBLIC.PAYMENTS_LOGIN}/pay/${params['token']}`
        );
      }
      return;
    }
    if (patientDetails?.id && statementsEnable) {
      setLoading(true);
      fetchPatientsInvoiceLines(
        patientDetails?.id,
        patientDetails?.clinic?.id ?? ''
      )
        .then(res => {
          setInvoiceLines(res?.data ?? []);
        })
        .catch(err => {
          if (err) {
            backNavigation();
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setInvoiceLines([]);
    }
  }, [patientDetails, params]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderInvoiceLineElement = ({
    item,
    index = 'index'
  }: {
    item: {
      amount: number;
      description: string;
      serviceDate: any;
    };
    index?: any;
  }) => {
    return (
      <Typography
        className=" payments-section-view input-header"
        key={index}
        component={'div'}>
        <div className="flex-view">
          {!isDesktop ? (
            <div className="description-date">
              <div>{HelperUtils.formatDisplayDate(item.serviceDate)}</div>
              <div style={{wordBreak: 'break-word'}}>{item.description}</div>
            </div>
          ) : (
            <>
              <div className="date-view">
                {HelperUtils.formatDisplayDate(item.serviceDate)}
              </div>
              <div className="left-view">{item.description}</div>
            </>
          )}
          <div className="amount-view">
            {item.amount !== null ? (
              <CurrencyFormatter
                value={item.amount || ''}
                displayType={'text'}
                thousandSeparator={true}
                prefix={'$'}
                ignoreRound={true}
                useSign={true}
              />
            ) : (
              ''
            )}
          </div>
        </div>
      </Typography>
    );
  };

  const renderPaymentSectionDetails = (): ReactNode => {
    if (invoiceLines?.length) {
      return (
        <GridTable
          columns={[
            {
              title: (
                <Trans
                  i18nKey={
                    'PAYMENTS_LOGIN.DASHBOARD.DETAILS.HEADERS.DATES_DESCRIPTION'
                  }
                  components={[<br key={'X'} />]}
                />
              ),
              field: 'serviceDate',
              render: (rowData: any) => {
                return (
                  <div className={classes.paymentDateView}>
                    <span className={classes.lineHeight}>
                      {HelperUtils.formatDisplayDate(rowData.service_date)}
                    </span>

                    {rowData.description && (
                      <span
                        className={[
                          classes.description,
                          classes.lineHeight
                        ].join(' ')}>
                        {rowData.description}
                      </span>
                    )}
                  </div>
                );
              },
              cellStyle: dateOfPaymentCell
            },
            {
              title: t('PAYMENTS_LOGIN.DASHBOARD.DETAILS.HEADERS.AMOUNT'),
              field: 'amount',
              cellStyle: {
                ...dateOfPaymentCell,
                ...headerRightAlign
              } as React.CSSProperties,
              headerStyle: {...headerRightAlign} as React.CSSProperties,
              render: (rowData: any) => (
                <div className="description-row">
                  {rowData.amount !== null ? (
                    <span className={classes.lineHeight}>
                      <CurrencyFormatter
                        value={rowData.amount || '0'}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={'$'}
                        ignoreRound={true}
                        useSign={true}
                      />
                    </span>
                  ) : (
                    <span />
                  )}
                </div>
              )
            }
          ]}
          options={{
            ...DEFAULT_GRID_TABLE__OPTIONS,
            search: false,
            showTitle: false,
            toolbar: false,
            sorting: false,
            paging: false,
            emptyRowsWhenPaging: false,
            showEmptyDataSourceMessage: true,
            maxBodyHeight: '100%',
            tableLayout: 'auto',
            selection: false,
            draggable: false,
            headerStyle: {...paymentTableHeader} as React.CSSProperties,
            rowStyle: (rowData: any, index) => {
              let style: React.CSSProperties = {};
              if (index % 2) {
                style = {...tableEvenRow} as React.CSSProperties;
              } else {
                style = {...tableOddRow} as React.CSSProperties;
              }
              if (rowData.start_of_new_visit === true) {
                style.borderTop = '2px solid grey';
              }
              return style as React.CSSProperties;
            }
          }}
          data={invoiceLines}
          style={{
            border: '1px solid lightgrey',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            boxShadow: '0px 0px white'
          }}
        />
      );
    } else {
      return <div />;
    }
  };

  return (
    <div className="payments-details-wrapper">
      <Card
        className={['payments-details-content-wrap', classes.card].join(' ')}>
        <CardContent
          className={[classes.cardPadding, 'content'].join(' ')}
          style={{backgroundColor: '#fcfcfc'}}>
          {/* Guarantor name view start */}
          {!isLoading && (
            <div
              className="payment-details-content-view"
              style={{paddingTop: 0}}>
              {!isDesktop && (
                <>
                  <Typography className="details-view" component={'div'}>
                    <BackNavigation navigateTo={backNavigation} />
                  </Typography>
                  {!statementsEnable && (
                    <Typography
                      style={{margin: '3rem 0 0.5rem 0'}}
                      className="right-info-text input-header"
                      component={'div'}>
                      {t('PAYMENTS_LOGIN.DASHBOARD.NO_INFO')}
                    </Typography>
                  )}
                </>
              )}
              <div className="body-values">
                {statementsEnable && (
                  <Typography
                    className="patient-name input-header"
                    component={'div'}>
                    {((patientDetails &&
                      Object.keys(patientDetails).length !== 0) ||
                      balanceForwardData) &&
                      t('PAYMENTS_LOGIN.DASHBOARD.RIGHT_INFO_TEXT', {
                        name:
                          patientDetails &&
                          Object.keys(patientDetails).length !== 0
                            ? HelperUtils.commaSeparatedFullName(
                                patientDetails?.first_name,
                                patientDetails?.last_name,
                                patientDetails?.middle_name
                              )
                            : HelperUtils.commaSeparatedFullName(
                                balanceForwardData?.guarantorDetails?.firstName,
                                balanceForwardData?.guarantorDetails?.lastName,
                                balanceForwardData?.guarantorDetails?.middleName
                              )
                      })}
                  </Typography>
                )}
                {statementsEnable ? (
                  <div>
                    {patientDetails &&
                    Object.keys(patientDetails).length !== 0 ? (
                      invoiceLines.length > 0 ? null : (
                        <Typography
                          className="right-info-text input-header"
                          component={'div'}
                          style={{padding: 0, margin: 0}}>
                          {t('PAYMENTS_LOGIN.DASHBOARD.NO_INVOICE')}
                        </Typography>
                      )
                    ) : balanceForwardData ? (
                      <div>
                        {renderInvoiceLineElement({
                          item: {
                            serviceDate: balanceForwardData.statementDate,
                            amount: balanceForwardData.amount,
                            description: t(
                              'PAYMENTS_LOGIN.DASHBOARD.BALANCE_FORWARD_DEFAULT_DESCRIPTION'
                            )
                          }
                        })}
                      </div>
                    ) : (
                      <Typography
                        style={{display: 'contents'}}
                        className="right-info-text input-header"
                        component={'div'}>
                        {t('PAYMENTS_LOGIN.DASHBOARD.PATIENTS_VISIT_NULL')}
                      </Typography>
                    )}
                  </div>
                ) : (
                  isDesktop && (
                    <Typography
                      style={{display: 'contents'}}
                      className="right-info-text input-header"
                      component={'div'}>
                      {t('PAYMENTS_LOGIN.DASHBOARD.NO_INFO')}
                    </Typography>
                  )
                )}
                {renderPaymentSectionDetails()}
              </div>
              {isDesktop && (
                <>
                  {clinicDetails?.clinic_group_details?.appointments && (
                    <AppointmentSection
                      scrollIntoView={'appointments' === sectionId}
                    />
                  )}
                  {clinicDetails?.clinic_group_details
                    ?.prior_authorizations_tickets && (
                    <PriorAuthorizationSection
                      scrollIntoView={'prior-authorizations' === sectionId}
                    />
                  )}
                </>
              )}
            </div>
          )}
          {isLoading && <Loader />}
        </CardContent>
      </Card>
    </div>
  );
};

function mapStateToProps(state: any): any {
  return {
    isLogin: state.payments.isLogin,
    patientPaymentDetailsId: state.payments.patientPaymentDetailsId
  };
}
export default connect(mapStateToProps, {
  _guarantorLogin,
  _patientPaymentDetailsId
})(PaymentsDetails);
